import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  CheckSquare, CheckCircle, XCircle, Clock, AlertTriangle,
  MessageSquare, ArrowRight, Loader2, Shield, Info
} from 'lucide-react';

export default function ApprovalQueuePage() {
  const [tab, setTab] = useState('all');
  const [approvals, setApprovals] = useState([]);
  const [slaSummary, setSlaSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  const loadApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const tier = tab === 'all' ? undefined : tab;
      const [approvalR, slaR] = await Promise.all([
        api.get('/approvals/pending', { params: { tier } }),
        api.get('/approvals/sla-summary'),
      ]);
      setApprovals(approvalR.data);
      setSlaSummary(slaR.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { loadApprovals(); }, [loadApprovals]);

  const handleAction = async (id, action, reason = '') => {
    setProcessing(true);
    try {
      await api.post(`/approvals/${id}`, { action, reason, comments: reason });
      setActionId(null);
      setActionType(null);
      setActionNote('');
      await loadApprovals();
    } catch (e) { console.error(e); }
    finally { setProcessing(false); }
  };

  const byTier = slaSummary?.by_tier || [];

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="approval-queue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Approval Queue</h1>
          <p className="text-sm text-slate-500 mt-1">Sequential 3-tier invoice approval workflow</p>
        </div>
        {slaSummary && (
          <Badge variant="outline" className="rounded-sm text-xs" data-testid="sla-compliance-badge">
            SLA Compliance: {slaSummary.compliance_rate}%
          </Badge>
        )}
      </div>

      {/* Tier Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tier-summary-cards">
        {byTier.map(t => (
          <Card key={t.tier} className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-slate-900 capitalize">Tier {t.tier_number}: {t.tier}</p>
                {t.breached > 0 && (
                  <Badge variant="outline" className="rounded-sm text-[10px] border-red-300 text-red-700 bg-red-50 animate-pulse">
                    {t.breached} SLA breached
                  </Badge>
                )}
              </div>
              <div className="flex gap-4 text-xs text-slate-500">
                <span>Pending: <b className="text-amber-600">{t.pending}</b></span>
                <span>Approved: <b className="text-green-600">{t.approved}</b></span>
                <span>Total: <b>{t.total}</b></span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm h-9 bg-slate-100 p-0.5">
          <TabsTrigger value="all" className="rounded-sm text-xs" data-testid="tab-all">All Tiers</TabsTrigger>
          <TabsTrigger value="operations" className="rounded-sm text-xs" data-testid="tab-operations">Operations</TabsTrigger>
          <TabsTrigger value="settlements" className="rounded-sm text-xs" data-testid="tab-settlements">Settlements</TabsTrigger>
          <TabsTrigger value="finance" className="rounded-sm text-xs" data-testid="tab-finance">Finance</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>
      ) : approvals.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="py-16 text-center">
            <CheckSquare className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
            <p className="mt-4 text-sm text-slate-400">No pending approvals in this tier</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="approvals-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Invoice</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Vendor</th>
                  <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Amount</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Tier</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">SLA</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Flags</th>
                  <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map(a => {
                  const now = new Date();
                  const deadline = a.sla_deadline ? new Date(a.sla_deadline) : null;
                  const hoursLeft = deadline ? Math.round((deadline - now) / (1000 * 60 * 60)) : null;
                  const breached = hoursLeft !== null && hoursLeft < 0;
                  const isActioning = actionId === a.id;

                  return (
                    <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/30" data-testid={`approval-row-${a.invoice_number}`}>
                      <td className="px-5 py-3">
                        <button onClick={() => navigate(`/invoices/${a.invoice_id}`)} className="text-xs font-mono font-bold text-[#007AFF] hover:underline">
                          {a.invoice_number}
                        </button>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600 max-w-[120px] truncate">{a.vendor_name}</td>
                      <td className="px-5 py-3 text-right font-mono text-xs font-bold">{a.currency} {parseFloat(a.total_amount || 0).toLocaleString()}</td>
                      <td className="px-5 py-3">
                        <Badge variant="outline" className="rounded-sm text-[10px] uppercase">{a.approval_tier}</Badge>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={`rounded-sm text-[10px] ${
                            breached ? 'border-red-300 text-red-700 bg-red-50' :
                            hoursLeft < 4 ? 'border-amber-300 text-amber-700 bg-amber-50' :
                            'border-green-300 text-green-700 bg-green-50'
                          }`}>
                            {breached ? `${Math.abs(hoursLeft)}h overdue` : hoursLeft !== null ? `${hoursLeft}h left` : '—'}
                          </Badge>
                          {a.escalated_at && (
                            <Badge variant="outline" className="rounded-sm text-[9px] border-red-400 text-red-600 bg-red-50 animate-pulse" data-testid={`escalated-${a.id}`}>
                              ESCALATED
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1">
                          {a.dispute_flag && (
                            <Badge variant="outline" className="rounded-sm text-[9px] border-purple-300 text-purple-700 bg-purple-50">DISPUTE</Badge>
                          )}
                          {a.request_info_note && (
                            <Badge variant="outline" className="rounded-sm text-[9px] border-blue-300 text-blue-700 bg-blue-50">INFO REQ</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        {isActioning ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={actionNote}
                              onChange={(e) => setActionNote(e.target.value)}
                              placeholder={actionType === 'reject' ? 'Reason...' : 'Note...'}
                              className="h-7 text-xs rounded-sm w-32"
                              data-testid="action-note-input"
                            />
                            <Button
                              size="sm"
                              onClick={() => handleAction(a.id, actionType, actionNote)}
                              disabled={processing}
                              className="h-7 rounded-sm text-[10px] bg-[#0A2463] text-white"
                              data-testid="action-confirm-button"
                            >
                              {processing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'OK'}
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => { setActionId(null); setActionType(null); setActionNote(''); }}
                              className="h-7 rounded-sm text-[10px]"
                            >
                              X
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => handleAction(a.id, 'approve')}
                              disabled={processing}
                              data-testid={`approve-${a.invoice_number}`}
                              className="h-7 rounded-sm text-[10px] text-green-700 hover:text-green-800 hover:bg-green-50"
                            >
                              <CheckCircle className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => { setActionId(a.id); setActionType('reject'); }}
                              data-testid={`reject-${a.invoice_number}`}
                              className="h-7 rounded-sm text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <XCircle className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => { setActionId(a.id); setActionType('request_info'); }}
                              data-testid={`info-${a.invoice_number}`}
                              className="h-7 rounded-sm text-[10px] text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Info className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => { setActionId(a.id); setActionType('dispute'); }}
                              data-testid={`dispute-${a.invoice_number}`}
                              className="h-7 rounded-sm text-[10px] text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            >
                              <AlertTriangle className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
